import React, { useEffect, useCallback, useRef } from "react";
import mermaid from "mermaid";
import svgPanZoom from "svg-pan-zoom";

export interface MermaidProps {
  text: string;
  className?: string;
  height?: string;
  width?: string;
  panZoomEnabled?: boolean;
}

export const Mermaid: React.FC<MermaidProps> = ({
  text,
  className = "w-full h-[calc(100vh_-_300px)] flex justify-center items-center",
  panZoomEnabled = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const panZoomInstanceRef = useRef<any>(null);
  const renderIdRef = useRef(`mermaid-${Math.random().toString(36).slice(2)}`);

  const initializeMermaid = useCallback(async () => {
    try {
      await mermaid.initialize({
        startOnLoad: true,
        securityLevel: "loose",
        theme: "base",
        logLevel: process.env.NODE_ENV === "development" ? "debug" : "error",
        themeVariables: {
          background: "#f0f0f0",
          primaryColor: "#fff",
          primaryBorderColor: "#000000",
          primaryBorderWidth: "2px",
          nodeBorder: "#CFD6DD",
          nodeBorderWidth: "2px",
          nodeTextColor: "#272E35",
          fontFamily: "Inter",
          fontSize: "14px",
          borderRadius: "10px",
          nodeRadius: "8px",
        },
      });
    } catch (error) {
      console.error("Failed to initialize mermaid:", error);
    }
  }, []);

  const renderDiagram = useCallback(async () => {
    if (!containerRef.current || !text) return;

    try {
      // Clear previous content and destroy previous pan-zoom instance
      containerRef.current.innerHTML = "";
      if (panZoomInstanceRef.current) {
        panZoomInstanceRef.current.destroy();
        panZoomInstanceRef.current = null;
      }

      try {
        await mermaid.parse(text);
      } catch (parseError) {
        throw new Error("Mermaid syntax error");
      }

      const { svg } = await mermaid.render(renderIdRef.current, text);

      if (
        svg.includes("Syntax error in text") ||
        svg.includes("mermaid version")
      ) {
        throw new Error("Mermaid syntax error");
      }

      if (containerRef.current) {
        containerRef.current.innerHTML = svg;

        if (panZoomEnabled) {
          const svgElement = containerRef.current.querySelector("svg");
          if (svgElement) {
            svgElement.style.height = "90%";
            panZoomInstanceRef.current = svgPanZoom(svgElement, {
              zoomEnabled: true,
              panEnabled: true,
              controlIconsEnabled: true,
              fit: true,
              center: true,
              minZoom: 1,
              maxZoom: 20,
            });
          }
        }
      }
    } catch (error) {
      console.error("Failed to render mermaid diagram:", error);

      if (containerRef.current) {
        containerRef.current.innerHTML = `
          <div class="flex flex-col items-center justify-center p-4 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#6B7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            <h3 class="mt-4 text-lg font-medium text-gray-700">Claim structure could not be rendered</h3>
          </div>
        `;
      }
    }
  }, [text, panZoomEnabled]);

  // Initialize mermaid once
  useEffect(() => {
    initializeMermaid();
  }, [initializeMermaid]);

  // Render diagram when text or panZoomEnabled changes
  useEffect(() => {
    const renderTimer = setTimeout(renderDiagram, 100);
    return () => clearTimeout(renderTimer);
  }, [text, renderDiagram, panZoomEnabled]);

  return (
    <div
      ref={containerRef}
      className={`mermaid-diagram ${className}`.trim()}
      data-testid="mermaid-container mx-auto"
      // style={{ height, width }}
    />
  );
};

export default Mermaid;
